import React, { useState } from 'react';
import { Users, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatSponsorName } from '../utils/formatSponsorName';
import InfoTooltip from './InfoTooltip';

const SponsorRow = ({ sponsor, isDarkMode, isPrimary, compact = false, className }) => {
    const navigate = useNavigate();
    const party = sponsor.party || '';
    const isDemocrat = party.toLowerCase().includes('democrat');
    const isRepublican = party.toLowerCase().includes('republican');
    const districtNumber = sponsor.district_number;

    // Get photo URL - check both camelCase and snake_case for compatibility
    const photoUrl = sponsor.photoUrl || sponsor.photo_url || null;

    // Get link - if politician, link to profile, otherwise link to legis.ga.gov
    // Check both camelCase and snake_case field names
    const getSponsorLink = () => {
        const politicianId = sponsor.politicianId || sponsor.politician_id;
        const websiteUrl = sponsor.websiteUrl || sponsor.website_url;
        const ggaMemberId = sponsor.ggaMemberId || sponsor.gga_member_id;
        const chamber = sponsor.chamber;

        if (politicianId) {
            return `/politicians/${politicianId}`;
        }
        // Prefer stored website_url, otherwise compute it
        if (websiteUrl) {
            return websiteUrl;
        }
        if (ggaMemberId && chamber) {
            const chamberPath = chamber.toLowerCase() === 'senate' ? 'senate' : 'house';
            return `https://www.legis.ga.gov/members/${chamberPath}/${ggaMemberId}`;
        }
        return null;
    };

    const sponsorLink = getSponsorLink();
    const isClickable = !!sponsorLink;

    const handleSponsorClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!sponsorLink) {
            console.log('No link available for sponsor:', sponsor);
            return;
        }

        console.log('Navigating to:', sponsorLink);
        if (sponsorLink.startsWith('http')) {
            // External link - open in new tab
            window.open(sponsorLink, '_blank', 'noopener,noreferrer');
        } else {
            // Internal link - navigate
            navigate(sponsorLink);
        }
    };

    return (
        <div
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            className={`flex items-center justify-between ${compact ? 'p-2' : 'p-2.5'} rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/80'} border ${isDarkMode ? 'border-gray-600/30' : 'border-gray-200/50'} ${isClickable ? 'cursor-pointer hover:border-blue-400 hover:shadow-md transition-all' : ''} ${className || ''}`.trim()}
            onClick={isClickable ? handleSponsorClick : undefined}
            onKeyDown={isClickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSponsorClick(e);
                }
            } : undefined}
        >
            <div className="flex items-center space-x-2 flex-1 min-w-0">
                {/* Photo or Party Badge */}
                {photoUrl ? (
                    <div className="relative flex-shrink-0">
                        <img
                            src={photoUrl}
                            alt={formatSponsorName(sponsor)}
                            className="w-14 h-14 rounded-full object-cover"
                            onError={(e) => {
                                // Hide image and show party badge fallback
                                e.target.style.display = 'none';
                                const badge = e.target.parentElement.querySelector('.party-badge-fallback');
                                if (badge) badge.style.display = 'inline-flex';
                            }}
                        />
                        {/* Party badge overlay on photo - small badge in corner */}
                        {party && (
                            <span
                                className={`absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold border-2 ${isDarkMode ? 'border-gray-800' : 'border-white'} ${isDemocrat
                                    ? 'bg-blue-600 text-white'
                                    : isRepublican
                                        ? 'bg-red-600 text-white'
                                        : 'bg-gray-600 text-white'
                                    }`}
                                title={party}
                            >
                                {party.substring(0, 1).toUpperCase()}
                            </span>
                        )}
                    </div>
                ) : (
                    /* Party badge - shown if no photo or if photo fails to load */
                    <span
                        className={`party-badge-fallback inline-flex items-center justify-center w-14 h-14 rounded-full text-xs font-bold flex-shrink-0 ${isDemocrat
                            ? isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                            : isRepublican
                                ? isDarkMode ? 'bg-red-600 text-white' : 'bg-red-100 text-red-800'
                                : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-700'
                            }`}
                    >
                        {party ? party.substring(0, 3).toUpperCase() : '?'}
                    </span>
                )}

                <div className="flex flex-col min-w-0 flex-1">
                    <span className={`${isDarkMode ? 'text-gray-200' : 'text-gray-900'} text-sm font-medium truncate ${isClickable ? 'hover:underline' : ''}`}>
                        {formatSponsorName(sponsor)}
                    </span>
                    {districtNumber && (
                        <span className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {districtNumber === 'Unknown' ? (
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-800'}`}>
                                    District Unknown
                                </span>
                            ) : (
                                `District ${districtNumber}`
                            )}
                        </span>
                    )}
                </div>
            </div>
            {isPrimary && (
                <span className="text-yellow-500 text-sm flex-shrink-0 ml-2" title="Sponsor">★</span>
            )}
        </div>
    );
};

const SponsorSection = ({ bill, isDarkMode }) => {
    const [showAllSponsors, setShowAllSponsors] = useState(false);

    // Debug: Log sponsor data to see what we're receiving
    React.useEffect(() => {
        if (bill.sponsors && bill.sponsors.length > 0) {
            console.log('SponsorSection - bill.sponsors:', bill.sponsors);
            console.log('First sponsor data:', bill.sponsors[0]);
        }
    }, [bill.sponsors]);

    if (!bill.sponsors || bill.sponsors.length === 0) {
        return (
            <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className={`w-12 h-12 mx-auto mb-3 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} flex items-center justify-center`}>
                    <Users className="w-6 h-6" />
                </div>
                No sponsors listed
            </div>
        );
    }

    // Separation logic
    // Primary if role is 'primary', 'Primary', OR order is 0 or 1 (some APIs vary)
    const primarySponsors = bill.sponsors.filter(s =>
        s.role?.toLowerCase() === 'primary' ||
        s.sequence === 1 ||
        s.order === 0 || s.order === 1
    );

    // A bill can only have one sponsor - take the first primary found or the first sponsor
    const sponsor = primarySponsors.length > 0 ? primarySponsors[0] : bill.sponsors[0];

    // Co-sponsors are everyone else (excluding the single sponsor)
    const coSponsors = bill.sponsors.filter(s => s !== sponsor);

    // Show first 4 co-sponsors when collapsed
    const visibleCoSponsors = showAllSponsors ? coSponsors : coSponsors.slice(0, 4);
    const hiddenCount = coSponsors.length - 4;

    return (
        <div className="space-y-4">
            {/* Sponsor (singular - the legislator who formally introduces the bill) */}
            <div>
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <InfoTooltip
                        content="The legislator who formally introduces the bill"
                        isDarkMode={isDarkMode}
                        size="sm"
                        underline={true}
                    >
                        Sponsor
                    </InfoTooltip>
                </h4>
                <div className="space-y-2">
                    <SponsorRow sponsor={sponsor} isDarkMode={isDarkMode} isPrimary={true} />
                </div>
            </div>

            {/* Co-Sponsors */}
            {coSponsors.length > 0 && (
                <div>
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <InfoTooltip
                            content="Legislators who support the bill but did not introduce it"
                            isDarkMode={isDarkMode}
                            size="sm"
                            underline={true}
                        >
                            Co-Sponsors
                        </InfoTooltip>
                        <span className={`ml-2 font-normal ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            ({coSponsors.length})
                        </span>
                    </h4>

                    {/* Collapsed view - show first 4 in a list */}
                    {!showAllSponsors && (
                        <>
                            <div className="space-y-2">
                                {visibleCoSponsors.map((cosponsor, idx) => (
                                    <SponsorRow key={`co-${idx}`} sponsor={cosponsor} isDarkMode={isDarkMode} />
                                ))}
                            </div>

                            {hiddenCount > 0 && (
                                <div className="mt-2">
                                    <button
                                        onClick={() => setShowAllSponsors(true)}
                                        className={`w-full py-2 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-blue-400' : 'bg-gray-100 hover:bg-gray-200 text-blue-600'
                                            }`}
                                    >
                                        <ChevronDown className="w-3 h-3" />
                                        See {hiddenCount} more co-sponsors
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* Expanded view - show all co-sponsors in a grid */}
                    {showAllSponsors && (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {coSponsors.map((cosponsor, idx) => (
                                    <SponsorRow
                                        key={`co-expanded-${idx}`}
                                        sponsor={cosponsor}
                                        isDarkMode={isDarkMode}
                                    />
                                ))}
                            </div>

                            <div className="mt-2">
                                <button
                                    onClick={() => setShowAllSponsors(false)}
                                    className={`w-full py-2 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${isDarkMode ? 'hover:bg-gray-800 text-gray-500' : 'hover:bg-gray-100 text-gray-500'
                                        }`}
                                >
                                    <ChevronUp className="w-3 h-3" />
                                    Show less
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export { SponsorRow };
export default SponsorSection;
